import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { MultiToasts, TOAST_POSITIONS, TOAST_VARIANTS } from 'cdk-radial';
import { TOAST_VISIBILITY_DURATIONS } from '../adapters/constants';
import { v4 } from 'uuid';
import StyledToast from './styled';

type ToastContext = {
  actions: { onClick: () => void; text: string }[];
  content: string;
  id: string;
  onVisibilityDurationEnd: () => void;
  variant: string;
  visibilityDuration: number;
};

type ContextValue = {
  toasts: JSX.Element;
  addSuccessToast: (content: string) => void;
  addWarningToast: (content: string) => void;
  addErrorToast: (content: string) => void;
  addInformationToast: (content: string) => void;
};

const ToastProviderContext = createContext<ContextValue | null>(null);

const ToastProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [toastItems, setToastItems] = useState<ToastContext[]>([]);

  const addToast = useCallback((toast: ToastContext) => {
    setToastItems(prev => {
      const existsAlready = prev.some(item => item.content === toast.content);
      if (existsAlready) return prev;
      return [...prev, toast];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToastItems(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toastObject = useCallback(
    ({
      content,
      variant
    }: {
      content: string;
      variant: string;
    }): ToastContext => {
      const id = v4();
      return {
        actions: [
          {
            onClick: () => removeToast(id),
            text: 'Dismiss'
          }
        ],
        content,
        id,
        onVisibilityDurationEnd: () => {
          removeToast(id);
        },
        variant,
        visibilityDuration: TOAST_VISIBILITY_DURATIONS.EIGHT_SECONDS
      };
    },
    [removeToast]
  );

  const addSuccessToast = useCallback(
    (content: string) => {
      const successToast = toastObject({
        content,
        variant: TOAST_VARIANTS.POSITIVE as string
      });
      addToast(successToast);
    },
    [addToast, toastObject]
  );

  const addWarningToast = useCallback(
    (content: string) => {
      const warningToast = toastObject({
        content,
        variant: TOAST_VARIANTS.WARNING as string
      });
      addToast(warningToast);
    },
    [addToast, toastObject]
  );

  const addErrorToast = useCallback(
    (content: string) => {
      const errorToast = toastObject({
        content,
        variant: TOAST_VARIANTS.NEGATIVE as string
      });
      addToast(errorToast);
    },
    [addToast, toastObject]
  );

  const addInformationToast = useCallback(
    (content: string) => {
      const informationToast = toastObject({
        content,
        variant: TOAST_VARIANTS.NEUTRAL as string
      });
      addToast(informationToast);
    },
    [addToast, toastObject]
  );

  const toasts = useMemo(
    () => (
      <StyledToast>
        <MultiToasts
          items={toastItems}
          position={TOAST_POSITIONS.INLINE as string}
        />
      </StyledToast>
    ),
    [toastItems]
  );

  const toastProviderValues = useMemo(
    () => ({
      toasts,
      addSuccessToast,
      addWarningToast,
      addErrorToast,
      addInformationToast
    }),
    [
      toasts,
      addSuccessToast,
      addWarningToast,
      addErrorToast,
      addInformationToast
    ]
  );

  return (
    <ToastProviderContext.Provider value={toastProviderValues}>
      {toasts}
      {children}
    </ToastProviderContext.Provider>
  );
};

export const useToastProvider = (): ContextValue => {
  const context = useContext(ToastProviderContext);
  if (!context) {
    throw new Error('useToastProvider must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
